/* import __COLOCATED_TEMPLATE__ from './billing-record-table.hbs'; */
import { A } from '@ember/array';
import { readOnly } from '@ember/object/computed';
import { computed } from '@ember/object';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { showModal } from 'sh-www/utils/modal-controls';
import Component from '@ember/component';
import { APPLICATION } from 'sh-www/models/application';
import { BILLABLE_RECORD } from "../models/billable-record";
import { SH_BILLABLE_LINE_ITEM } from "../models/sh-billable-line-item";
import { FEE_STRUCTURE } from 'sh-www/models/fee-structure';

const CLASS_NAME = 'SH-c-billing-record-table';

/** Displays the line items in a billing record (e.g. invoice or payment). */
const BillingRecordTable = Component.extend({
  session: service(),
  currentUser: readOnly('session.currentUser'),

  tagName: '',
  classNames: [CLASS_NAME],
  CLASS_NAME,

  /** @param {FeeStructure=} Optional fee structure record used by fee breakdown */
  agnosticFeeStructure: undefined,

  /** @param {Boolean} allowExpenseAbilities Whether this component shows add/update expense when available  */
  allowExpenseAbilities: false,

  /** @param {Application} application If this is being shown in completion of an entire project */
  application: undefined,

  /** @param {Boolean} approvingAdvanceBillingRecord Whether user is approving an advance. */
  approvingAdvanceBillingRecord: false,

  /** @param {ShPayment|ShInvoice} billingRecord The billing record object */
  billingRecord: undefined,

  /** @param {Boolean} isLoading Whether the data is currently being fetched. */
  isLoading: true,

   /** @param {Boolean} makingCounterOffer Whether the data is about a counter offer being made. */
  makingCounterOffer: false,

  /** @param {Boolean} showExpediteOption Whether to display the expedite option to the user. */
  showExpediteOption: true,

  /** @param {Boolean} showFeeBreakdown Whether to show the fee breakdown. */
  showFeeBreakdown: true,

  /** @param {String} text The text to display above the table. */
  text: '',

  /** @param {Function} handleAddExpediteClick The click handler for the add expedite link. */
  handleAddExpediteClick: () => {},

  /** @param {Function} handleRemoveExpediteClick The click handler for the remove expedite link. */
  handleRemoveExpediteClick: () => {},

  /** @param {Function} handleTakeoverExpediteFeeClick The click handler for taking over expedite fees. */
  handleTakeoverExpediteFeeClick: () => {},

  /**  @param {Function} handleRefresh refresh state */
  handleRefresh: () => {},

  /** @type {String} The text to display that prompts the user to expedite. */
  _expenseReminderText: computed(
    'currentUser.isSeller',
    'application.isAllowExpenses',
    'allowExpenseAbilities',
    function () {
      return this.currentUser.isSeller
        && this.application.isAllowExpenses
        && this.allowExpenseAbilities
        ? 'This must include any and all expenses.'
        : '';
  }),

  /** @type {Boolean} Whether the project is being cancelled. */
  _isCancelled: computed(
    'billingRecord.shBillableLineItems.@each.type',
    function () {
      const shBillableLineItems = this.get('billingRecord.shBillableLineItems');
      if (!shBillableLineItems) {
        return false;
      }

      return !!shBillableLineItems.findBy('type', SH_BILLABLE_LINE_ITEM.TYPES.CANCELLATION);
    }
  ),

  /** @type {string} */
  _baseLabel: computed(
    '_isCancelled',
    '_baseAmount',
    'application.price',
    'billingRecord.type',
    'billingRecord.baseDescription',
    'makingCounterOffer',
    function () {
      if (this.billingRecord?.type === BILLABLE_RECORD.TYPES.PROJECT) {
        if (
          !this.makingCounterOffer
          && (
            this._isCancelled
            || (this.application?.price && this.application.price !== this._baseAmount)
          )
        ) {
          return 'Remaining project price';
        }
        return 'Project price';
      }

      return this.get('billingRecord.baseDescription') || 'Project price';
    }
  ),

  /** @type {Ember.Object} The base project price. */
  _baseAmount: computed(
    'billingRecord.{baseAmount,cancellationAmount}',
    function () {
      return this.get('billingRecord.baseAmount') + this.get('billingRecord.cancellationAmount');
    }
  ),

  _consolidatedFees: computed(
    'agnosticFeeStructure.buyerBaseFee',
    'application.sellerFeesCovered',
    'billingRecord.baseAmount',
    'billingRecord.consolidatedFees.@each.{amount,type}',
    'showFeeBreakdown',
    function() {
      // no breakdown to show
      if (
        !this.showFeeBreakdown
        || !this.application.sellerFeesCovered
        || !this.agnosticFeeStructure
      ) {
        // return without changes
        return this.billingRecord?.consolidatedFees ?? [];
      }

      const { baseAmount, consolidatedFees } = this.billingRecord;
      const { buyerBaseFee } = this.agnosticFeeStructure;

      // base fee can be broken down
      const baseFeeIndex = consolidatedFees.findIndex(({ type }) => type === FEE_STRUCTURE.FEE_TYPES.BASE_FEE);
      const totalBaseFee = consolidatedFees[baseFeeIndex].amount;

      // subtract covered seller fees from base fee for breakdown
      const standardBuyerServiceFee = Math.round(buyerBaseFee * baseAmount * 100) / 100;
      const coverCreatorFee = totalBaseFee - standardBuyerServiceFee;

      // modify local copy of consolidatedFees
      const updatedConsolidatedFees = [...consolidatedFees];
      const feeToBreakdown =  { ...updatedConsolidatedFees[baseFeeIndex] };
      feeToBreakdown.amount = standardBuyerServiceFee;

      // break apart single line item into multiple
      updatedConsolidatedFees[baseFeeIndex] = feeToBreakdown;
      updatedConsolidatedFees.push({
        type: SH_BILLABLE_LINE_ITEM.BREAKDOWN_TYPES.COVERED_CREATOR_FEE,
        description: SH_BILLABLE_LINE_ITEM.BREAKDOWN_TYPE_DESCRIPTIONS[SH_BILLABLE_LINE_ITEM.BREAKDOWN_TYPES.COVERED_CREATOR_FEE],
        amount: coverCreatorFee,
      });

      return updatedConsolidatedFees;
    }
  ),

  /** Credit amount in cents able to use on billing amount */
  _creditAppliedInCents: computed(
    'application.costCode.availableCreditInCents',
    'totalAmount',
    function() {
      return Math.round(Math.min((this.totalAmount * 100), this.application.costCode?.availableCreditInCents ?? 0));
    }
  ),

  /** Credit amount in cents able to use on billing amount */
  _defaultCreditAppliedInCents: computed(
    '_creditAppliedInCents',
    'application.buyer.company.defaultCostCode.availableCreditInCents',
    'totalAmount',
    function() {
      return Math.round(Math.min(
        (this.totalAmount * 100) - this._creditAppliedInCents,
        this.application.buyer.company.defaultCostCode?.availableCreditInCents ?? 0
      ));
    }
  ),

  _showCreditTotal: computed(
    'totalAmount',
    'currentUser.isSeller',
    'showCreditTotal',
    '_balanceAfterCreditAppliedInCents',
    function () {
      return this.showCreditTotal
        && !this.currentUser.isSeller
        && this._balanceAfterCreditAppliedInCents < Math.round(this.totalAmount * 100);
    }
  ),

  _coveredByCredit: computed(
    'expenseFeeAmount',
    'billingRecord.amount',
    'application.costCode.availableCreditInCents',
    'application.buyer.company.defaultCostCode.availableCreditInCents',
    function() {
      const totalWithCreditInCents = Math.round(
        ((this.billingRecord?.amount ?? 0) * 100)
        - (this.expenseFeeAmount * 100)
      );
      const availableCredit = (
        (this.application.costCode?.availableCreditInCents ?? 0)
        + (this.application.buyer.company.defaultCostCode?.availableCreditInCents ?? 0)
      );
      return totalWithCreditInCents <= availableCredit;
    }
  ),

  /** Billing amount remaining after max credit applied */
  _balanceAfterCreditAppliedInCents: computed(
    'totalAmount',
    '_creditAppliedInCents',
    '_defaultCreditAppliedInCents',
    function() {
      return (this.totalAmount * 100) - this._creditAppliedInCents - this._defaultCreditAppliedInCents;
    }
  ),

  /** Credit balance remaining after billing amount applied */
  _creditBalanceRemainingAfterAppliedInCents: computed(
    'application.costCode.availableCreditInCents',
    '_creditAppliedInCents',
    function() {
      return this.application.costCode.availableCreditInCents - this._creditAppliedInCents;
    }
  ),

  _isCoveredByCredit: computed(
    '_balanceAfterCreditAppliedInCents',
    function () {
      return this._balanceAfterCreditAppliedInCents <= 0;
    }
  ),

  /** @type {Ember.A} The expense line items. */
  _expenseLineItems: readOnly('billingRecord.expenseShBillableLineItems'),

  /**
   * Array of expenses
   *
   * @type {Ember.A<BillableRecord>}
   */
  expenseFeeAmount: computed(
    'billingRecord.shBillableLineItems.@each.{amount,type}',
    function() {
      if (!this.billingRecord) {
        return 0;
      }

      return this.billingRecord.shBillableLineItems.reduce(function (carry, shBillableLineItem) {
        if (shBillableLineItem.type === SH_BILLABLE_LINE_ITEM.TYPES.EXPEDITE_FEE) {
          carry += shBillableLineItem.amount;
        }
        return carry;
      }, 0);
    }
  ),

  totalAmount: computed(
    '_coveredByCredit',
    'billingRecord.amount',
    '_balanceAfterCreditAppliedInCents',
    'expenseFeeAmount',
    function() {
      if (this._coveredByCredit) {
        return (this.billingRecord?.amount ?? 0) - this.expenseFeeAmount;
      }
      return this.billingRecord?.amount ?? 0;
    }
  ),

  /** @type {Ember.A} The fee line items. */
  _feeLineItems: computed(
    '_consolidatedFees.@each.{amount,type}',
    function () {
      // Remove expedite fee from fees to render below
      return this._consolidatedFees.filter((item) => {
        // expedite fee shown in emphasized UI element
        if (
          item.type === SH_BILLABLE_LINE_ITEM.TYPES.EXPEDITE_FEE
          && this.showExpediteOption
        ) {
          return false;
        }

        return Math.abs(parseFloat(item.amount)) >= 0.01;
      });
    }
  ),

  /** @type {Ember.A} The other line items. */
  _otherItems: computed(
    'billingRecord.shBillableLineItems.@each.type',
    function () {
      const shBillableLineItems = this.get('billingRecord.shBillableLineItems');
      if (!shBillableLineItems) {
        return A([]);
      }

      return shBillableLineItems.filter((item) => {
        return item.get('type') === SH_BILLABLE_LINE_ITEM.TYPES.ADJUSTMENT;
      });
    }
  ),

  _showAddExpenses: computed(
    '_expenseLineItems.[]',
    'currentUser.isSeller',
    'application.{isAllowExpenses,status}',
    'allowExpenseAbilities',
    function () {
      const { isAllowExpenses, status } = this.application;
      return this.currentUser.isSeller
        && this.allowExpenseAbilities
        && this._expenseLineItems.length === 0
        && isAllowExpenses
        && APPLICATION.STATUS.ACTIVE === status;
  }),

  _showUpdateExpenses: computed(
    '_expenseLineItems.[]',
    'currentUser.isSeller',
    'application.{isAllowExpenses,status}',
    'allowExpenseAbilities',
    function () {
      const { isAllowExpenses, status } = this.application;
      return this.currentUser.isSeller
        && this.allowExpenseAbilities
        && this._expenseLineItems.length > 0
        && isAllowExpenses
        && APPLICATION.STATUS.ACTIVE === status;
  }),

  /** @type {string} */
  _totalLabel: computed(function () {
    return 'Total';
  }),

  @action
  toggleExpedite(isChecked, takeoverFee = null) {
    if (takeoverFee != null) {
      this.handleTakeoverExpediteFeeClick(takeoverFee);
    } else if (isChecked) {
      this.handleRemoveExpediteClick();
    } else {
      this.handleAddExpediteClick();
    }
  },

  /**
   * Called when user clicks to edit expenses.
   */
  @action
  onEditExpensesClicked() {
    const modalModel = {
      application: this.application,
      onExpensesUpdated: () => {
        // update test billing model
        this.handleRefresh();
      }
    };

    showModal(this, 'projects/edit-expenses-modal', modalModel);
  },
});

export default BillingRecordTable;
